import React, { createContext, type ReactNode, useContext } from 'react';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { ExtensionMode } from '@atlassian/company-hub-common/types';

import type { HubForgeExtensionParameters } from '../../types';

/**
 * Shape of the Forge renderer that the host (the `@atlassian/hub` package)
 * injects into the extension. The renderer is responsible for resolving the
 * Forge app for the given hub slot and rendering it.
 *
 * Keeping the renderer behind a context boundary lets `@atlassian/hub-forge-extension`
 * stay free of any direct Forge SDK dependency, which mirrors how Help Center
 * isolates its Forge integration today and keeps the extension package
 * cheap to lazy-load.
 */
export type HubForgeRenderer = (props: {
	contentId: string;
	extensionNode: ExtensionParams<HubForgeExtensionParameters>;
	mode: ExtensionMode;
}) => React.JSX.Element | null;

const HubForgeRendererContext = createContext<HubForgeRenderer | undefined>(undefined);

export type HubForgeRendererProviderProps = {
	children: ReactNode;
	renderer: HubForgeRenderer | undefined;
};

/**
 * Provider that the host (e.g. `@atlassian/hub`) wraps around the editor /
 * renderer subtree to make a concrete Forge renderer available to
 * `<HubForgeExtension />`.
 *
 * When no renderer is provided the consumer falls back to the empty state.
 */
export const HubForgeRendererProvider = ({
	children,
	renderer,
}: HubForgeRendererProviderProps): React.JSX.Element => (
	<HubForgeRendererContext.Provider value={renderer}>{children}</HubForgeRendererContext.Provider>
);

/**
 * Read the injected Forge renderer. Returns `undefined` if none was provided,
 * which the extension component treats as the empty state ("No forge app
 * available") so the editor still renders deterministically.
 */
export const useHubForgeRenderer = (): HubForgeRenderer | undefined =>
	useContext(HubForgeRendererContext);
